import React, {
	HTMLAttributes,
	PropsWithChildren,
	ReactNode,
	Ref,
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import { classNames } from 'primereact/utils';
import { Icon } from 'components/_new/Icon';
import './FieldAlert.scss';

const DEFAULT_DISPLAY_DURATION_MS = 2000;
const EXIT_DURATION_MS = 500;

type FieldAlertPhase = 'hidden' | 'enter' | 'exit';

export interface FieldAlertRef {
	show: () => void;
	hide: () => void;
}

export type FieldAlertVariant = 'success' | 'danger';

export type FieldAlertProps = PropsWithChildren<
	Pick<HTMLAttributes<HTMLDivElement>, 'className'> & {
		message?: ReactNode;
		absolute?: boolean;
		variant?: FieldAlertVariant;
		icon?: string;
		displayDurationMs?: number;
	}
>;

export const FieldAlert = forwardRef(
	(
		{
			className,
			message,
			children,
			absolute = false,
			variant = 'success',
			icon,
			displayDurationMs = DEFAULT_DISPLAY_DURATION_MS,
		}: FieldAlertProps,
		ref: Ref<FieldAlertRef>
	) => {
		const [phase, setPhase] = useState<FieldAlertPhase>('hidden');
		const [animationKey, setAnimationKey] = useState(0);
		const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
		const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
		const content = message || children;
		const resolvedIcon = icon || (variant === 'danger' ? 'triangle-exclamation' : 'check');

		const clearTimers = () => {
			if (hideTimerRef.current) {
				clearTimeout(hideTimerRef.current);
				hideTimerRef.current = null;
			}
			if (exitTimerRef.current) {
				clearTimeout(exitTimerRef.current);
				exitTimerRef.current = null;
			}
		};

		const hide = () => {
			clearTimers();
			setPhase('hidden');
		};

		const show = () => {
			clearTimers();
			setAnimationKey((prev) => prev + 1);
			setPhase('enter');

			hideTimerRef.current = setTimeout(() => {
				setPhase('exit');
			}, displayDurationMs);

			exitTimerRef.current = setTimeout(() => {
				setPhase('hidden');
			}, displayDurationMs + EXIT_DURATION_MS);
		};

		useImperativeHandle(
			ref,
			() => ({
				show,
				hide,
			}),
			[displayDurationMs]
		);

		useEffect(
			() => () => {
				clearTimers();
			},
			[]
		);

		if (!content || phase === 'hidden') {
			return null;
		}

		return (
			<div
				key={animationKey}
				className={classNames(
					'field-alert',
					`field-alert-${variant}`,
					{ 'field-alert-absolute': absolute },
					`field-alert-${phase}`,
					className
				)}
			>
				<span className="field-alert-icon-wrap" aria-hidden>
					<Icon name={resolvedIcon} className="field-alert-icon" />
				</span>
				{content}
			</div>
		);
	}
);
